// Copyright (c) 2023-2024 Broadcom. All Rights Reserved.
// Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
// and/or its subsidiaries.

import { Injectable, OnDestroy } from "@angular/core";
import { ApiService } from "@api-context/services/api/api.service";
import { MaintenanceScheduleOperatorGetResponseSpec, MaintenanceScheduleStateEnum, MaintenanceScheduleTenantGetResponseSpec } from "@api/generated/vcdr";
import { Dispatcher } from "@app/services/dispatcher/dispatcher";
import { MaintenanceModeEvent } from "@app/services/dispatcher/events/maintenance-mode-event";
import { EMPTY, Observable, ReplaySubject, Subject } from "rxjs";
import { catchError, mergeMap, take, takeUntil } from "rxjs/operators";
import { TimerService } from "@app/services/timer.service";

export const MINUTE = 60 * 1000;
export const HOUR = 60 * MINUTE;
export const THIRTY_MINUTES = 30 * MINUTE;
export const TEN_MINUTES = 10 * MINUTE;
export const DAY = 24 * HOUR;

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class MaintenanceModeService implements  OnDestroy {

    private apiService: ApiService;
    private pollInterval: number = 0;
    private scheduleChanged: boolean = false;
    private lastState: MaintenanceScheduleStateEnum;
    private startDateTimeMs: number;
    private endDateTimeMs: number;

    private unsubscribeSubject: Subject<void> = new ReplaySubject<void>(1);

    constructor(private timerService: TimerService,
                private dispatcher: Dispatcher) {
    }

    public initMaintenanceService(apiService: ApiService): void {
        this.apiService = apiService;
        this.pollMaintenanceMode()
            .pipe(takeUntil(this.unsubscribeSubject))
            .subscribe();
    }

    private pollMaintenanceMode(): Observable<any> {
        if (this.pollInterval >= 0) {
            return this.apiService.maintenanceService.getMaintenanceSchedule()
                .pipe(
                    takeUntil(this.unsubscribeSubject),
                    mergeMap(
                        schedule => {
                            this.updateSchedule(schedule);
                            this.calculateNextPollInterval();
                            return this.timerService.getTimerSource(this.pollInterval)
                                .pipe(
                                    take(1),
                                    mergeMap(() => this.pollMaintenanceMode())
                                );
                        }),
                    catchError(
                        error => {
                            console.log("Error polling maintenance schedule. Retrying in %d minutes...",
                                        TEN_MINUTES / MINUTE);
                            this.pollInterval = TEN_MINUTES;
                            return this.timerService.getTimerSource(this.pollInterval)
                                .pipe(
                                    take(1),
                                    mergeMap(() => this.pollMaintenanceMode())
                                );
                        }
                    )
                );
        }
        return EMPTY;
    }

    private updateSchedule(schedule: MaintenanceScheduleTenantGetResponseSpec | MaintenanceScheduleOperatorGetResponseSpec) {

        if (schedule.start_datetime) {
            const newTime: number = new Date(schedule.start_datetime).getTime();
            if (newTime != this.startDateTimeMs) {
                this.startDateTimeMs = newTime;
                this.scheduleChanged = true;
            }
        }

        if (schedule.end_datetime) {
            const newTime: number = new Date(schedule.end_datetime).getTime();
            if (newTime != this.endDateTimeMs) {
                this.endDateTimeMs = newTime;
                this.scheduleChanged = true;
            }
        }

        if (schedule.state != this.lastState) {
            this.lastState = schedule.state;

            if (schedule.state === MaintenanceScheduleStateEnum.NONE) {
                this.startDateTimeMs = 0;
                this.endDateTimeMs = 0;
            }

            this.scheduleChanged = true;
        }

        if (this.scheduleChanged) {
            this.dispatcher.publishEvent(new MaintenanceModeEvent(schedule));
        }
    }

    private calculateNextPollInterval() {
        const nowMs: number = new Date().getTime();
        const msToStart = this.startDateTimeMs - nowMs;
        const msToEnd = this.endDateTimeMs - nowMs;

        switch (this.lastState) {
            case MaintenanceScheduleStateEnum.CONFIRMED:
                // this state lasts 2 days, but can be cancelled
                if (msToStart > HOUR) {
                    this.pollInterval = THIRTY_MINUTES;
                } else if (msToStart > THIRTY_MINUTES) {
                    this.pollInterval = TEN_MINUTES;
                } else {
                    this.pollInterval = MINUTE;
                }
                break;
            case MaintenanceScheduleStateEnum.STARTED:
                // maintenance can be completed
                if (msToEnd > THIRTY_MINUTES) {
                    this.pollInterval = TEN_MINUTES;
                } else {
                    this.pollInterval = MINUTE;
                }
                break;
            case MaintenanceScheduleStateEnum.SCHEDULED:
                if (msToStart > 3 * DAY) {
                    this.pollInterval = DAY;
                } else {
                    this.pollInterval = THIRTY_MINUTES;
                }
                break;
            case MaintenanceScheduleStateEnum.CANCELLED:
            case MaintenanceScheduleStateEnum.COMPLETED:
            case MaintenanceScheduleStateEnum.NONE:
            default:
                this.pollInterval = -1;
                break;
        }
    }

    ngOnDestroy(): void {
        this.unsubscribeSubject.next();
        this.unsubscribeSubject.complete();
    }
}
